import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SubmissionGeom, SubmissionDetails } from '../../pages/submissions/submission.model';
import { saveAs } from 'file-saver';
import { XyCheckReport } from '../../pages/submissions/xy-check-report.model';
import { XyCheckResult } from '../../pages/submissions/xy-check-result.model';
import { XyCheckRequestData } from '../../pages/submissions/xy-check.model';
import { SaveDraftResult } from '../../pages/submissions/map/save-draft-result.model';
import { EditSubmissionRequest } from '@amdb/drone/shared/models/edit-submission-request.model';
import { lastValueFrom } from 'rxjs';
import { CommandResult, ZoneTypes, ZoneVariantTypes } from '@amdb/drone/shared/models';
import { TextType } from '@amdb/drone/shared/models/text-type.model';
import { CheckCaaRestrictionAreaCodeRequest } from '@amdb/drone/shared/models/check-caa-restriction-area-code-request.model';
import { GeomModel } from '@amdb/drone/shared/models/geom.model';

@Injectable({ providedIn: 'root' })
export class SubmissionsService {
    constructor(private httpClient: HttpClient) {}

    getDetails(submissionId: number): Promise<CommandResult<SubmissionDetails>> {
        return this.httpClient.get<CommandResult<SubmissionDetails>>(`/api/v1/submissions/${submissionId}/details`).toPromise();
    }

    uploadShpArchive(formData): Promise<any> {
        return this.httpClient.post('/api/v1/import', formData).toPromise();
    }

    uploadZoneFromMap(geomModel: GeomModel): Promise<any> {
        return this.httpClient.post('api/v1/import/from-map', geomModel).toPromise();
    }

    uploadZoneFromCoords(geomEwkt): Promise<any> {
        return this.httpClient.post('api/v1/import/from-coords', geomEwkt).toPromise();
    }

    saveDraft(submission: SubmissionDetails): Promise<SaveDraftResult> {
        return this.httpClient.post<SaveDraftResult>('/api/v1/submissions/new-draft', submission).toPromise();
    }

    deleteFailedDraftImportDirectory(directoryPath: string): Promise<void> {
        const replacedString = directoryPath.split('\\').join('&&');
        return this.httpClient.delete<void>(`/api/v1/import/failed/${replacedString}`).toPromise();
    }

    editDraft(submission: SubmissionDetails): Promise<void> {
        return this.httpClient.post<void>('/api/v1/submissions/edit-draft', submission).toPromise();
    }

    editSubmission(editSubmissionRequest: EditSubmissionRequest): Promise<void> {
        return this.httpClient.post<void>('/api/v1/submissions/edit', editSubmissionRequest).toPromise();
    }

    deleteDraft(submissionId: number): Promise<void> {
        return this.httpClient.delete<void>(`/api/v1/submissions/${submissionId}/draft`).toPromise();
    }

    changeStatus(action: string, submissionId: number, statusChange: FormData): Promise<void> {
        return this.httpClient.post<void>(`/api/v1/submissions/${submissionId}/${action}`, statusChange).toPromise();
    }

    downloadAttachment(submissionId: number, statusChangeHistoryId: number, attachmentName: string): Promise<void> {
        return this.httpClient
            .get<void>(`/api/v1/submissions/${submissionId}/${statusChangeHistoryId}/${attachmentName}`, { responseType: 'blob' as 'json', observe: 'response' })
            .toPromise()
            .then(res => {
                try {
                    const contentDisposition = res.headers.get('content-disposition');
                    const fileName = contentDisposition
                        .split(';')[1]
                        .split('filename')[1]
                        .split('=')[1]
                        .trim();
                    saveAs(res.body, fileName.replace(/^\"+|\"+$/g, ''));
                } catch {
                    console.error("Couldn't save file");
                }
            });
    }

    downloadOriginalfile(submissionId: number): Promise<void> {
        return this.httpClient
            .get<void>(`/api/v1/submissions/${submissionId}/original-file`, { responseType: 'blob' as 'json', observe: 'response' })
            .toPromise()
            .then(res => {
                try {
                    const contentDisposition = res.headers.get('content-disposition');
                    const fileName = contentDisposition
                        .split(';')[1]
                        .split('filename')[1]
                        .split('=')[1]
                        .trim();
                    saveAs(res.body, fileName.replace(/^\"+|\"+$/g, ''));
                } catch {
                    console.error("Couldn't save file");
                }
            });
    }

    exportGeoJson(approvedOnly: boolean, zones: boolean): Promise<void> {
        return this.httpClient
            .get<void>(`/api/v1/submissions/export-geojson/${approvedOnly}/${zones}`, { responseType: 'blob' as 'json', observe: 'response' })
            .toPromise()
            .then(res => {
                try {
                    const contentDisposition = res.headers.get('content-disposition');
                    const fileName = contentDisposition
                        .split(';')[1]
                        .split('filename')[1]
                        .split('=')[1]
                        .trim();
                    saveAs(res.body, fileName.replace(/^\"+|\"+$/g, ''));
                } catch {
                    console.error("Couldn't save file");
                }
            });
    }

    exportSubmissionList(onlyActive: boolean, disableableAuthorityId: number): Promise<void> {
        return this.httpClient
            .get(`/api/v1/submissions/submission-list-export/${onlyActive}/${disableableAuthorityId}`, { responseType: 'blob' as 'json', observe: 'response' })
            .toPromise()
            .then(res => {
                try {
                    const contentDisposition = res.headers.get('content-disposition');
                    const fileName = contentDisposition
                        .split(';')[1]
                        .split('filename')[1]
                        .split('=')[1]
                        .trim();
                    saveAs(res.body, fileName.replace(/^\"+|\"+$/g, ''));
                } catch {
                    console.error("Couldn't save file");
                }
            });
    }

    isOriginator(): Promise<boolean> {
        return this.httpClient.get<boolean>('/api/v1/submissions/is-originator').toPromise();
    }

    isPublisher(): Promise<boolean> {
        return this.httpClient.get<boolean>('/api/v1/submissions/is-publisher').toPromise();
    }

    hasToFlyAuthorities(): Promise<boolean> {
        return this.httpClient.get<boolean>('/api/v1/submissions/has-toFlyAuthorities').toPromise();
    }

    getGeoms(active: boolean, unapproved: boolean, inactive: boolean): Promise<SubmissionGeom[]> {
        return this.httpClient.get<SubmissionGeom[]>(`/api/v1/submissions/geoms/${active}/${unapproved}/${inactive}`).toPromise();
    }

    checkXy(xyCheckModels: XyCheckRequestData[]): Promise<XyCheckResult[]> {
        return this.httpClient.post<XyCheckResult[]>(`/api/v1/submissions/xy-check`, xyCheckModels).toPromise();
    }

    openXyCheckCsv(formData): Promise<XyCheckRequestData[]> {
        return this.httpClient.post<XyCheckRequestData[]>('/api/v1/submissions/xy-check/open-csv', formData).toPromise();
    }

    downloadPdf(reportData: XyCheckReport): Promise<void> {
        return this.httpClient
            .post<void>('/api/v1/submissions/xy-check/download-report', reportData, { responseType: 'blob' as 'json', observe: 'response' })
            .toPromise()
            .then(res => {
                try {
                    const contentDisposition = res.headers.get('content-disposition');
                    const fileName = contentDisposition
                        .split(';')[1]
                        .split('filename')[1]
                        .split('=')[1]
                        .trim();
                    saveAs(res.body, fileName.replace(/^\"+|\"+$/g, ''));
                } catch {
                    console.error("Couldn't save file");
                }
            });
    }

    downloadCrc32QInputString(id: number): Promise<void> {
        return this.httpClient
            .get(`/api/v1/submissions/crc-input-string/${id}`, { responseType: 'blob' as 'json', observe: 'response' })
            .toPromise()
            .then(res => {
                try {
                    const contentDisposition = res.headers.get('content-disposition');
                    const fileName = contentDisposition
                        .split(';')[1]
                        .split('filename')[1]
                        .split('=')[1]
                        .trim();
                    saveAs(res.body, fileName.replace(/^\"+|\"+$/g, ''));
                } catch {
                    console.error("Couldn't save file");
                }
            });
    }

    getAutoSubmissionCaaRACode(): Promise<CommandResult<TextType[]>> {
        return this.httpClient.get<CommandResult<TextType[]>>('/api/v1/submissions/auto-ra-code').toPromise();
    }

    checkCaaaRestrictionAreaCode(checkCaaRestrictionAreaCodeRequest: CheckCaaRestrictionAreaCodeRequest): Promise<CommandResult<boolean>> {
        return this.httpClient.post<CommandResult<boolean>>(`/api/v1/submissions/ra-code-check`, checkCaaRestrictionAreaCodeRequest).toPromise();
    }

    retrySendToBgkisAsNew(submissionId: number): Promise<void> {
        return this.httpClient.post<void>(`/api/v1/submissions/retry-send-as-new/${submissionId}`, null).toPromise();
    }

    retrySendToBgkisAsEdit(submissionId: number): Promise<void> {
        return this.httpClient.post<void>(`/api/v1/submissions/retry-send-as-edit/${submissionId}`, null).toPromise();
    }

    setStatusAsNextExportStatus(submissionId: number, restrictionAreaHistoryId: number): Promise<void> {
        return this.httpClient.post<void>(`/api/v1/submissions/set-next-export-status/${submissionId}/${restrictionAreaHistoryId}`, null).toPromise();
    }

    unsetStatusAsNextExportStatus(submissionId: number): Promise<void> {
        return this.httpClient.post<void>(`/api/v1/submissions/unset-next-export-status/${submissionId}`, null).toPromise();
    }

    async toggleIsInHiddenTab(submissionId: number): Promise<CommandResult<void>> {
        return await lastValueFrom(this.httpClient.put<CommandResult<void>>(`/api/v1/submissions/toggle-is-in-hidden-tab/${submissionId}`, null));
    }

    regenerateSubmissionsFirstStatusCrcInputString(): Promise<void> {
        return this.httpClient.post<void>('/api/v1/submissions/regenerate-first-status-crc', null).toPromise();
    }

    async copySubmission(submissionId: number): Promise<CommandResult<number>> {
        return await lastValueFrom(this.httpClient.post<CommandResult<number>>(`/api/v1/submissions/${submissionId}/copy`, null));
    }

    submissionHasRestrictionConditionsApplicable(submission: SubmissionDetails): boolean {
        const isProhibited: boolean = submission.uasZoneVersion.typeId == ZoneTypes.Prohibited;
        const isCustomizedProhibited: boolean = isProhibited && submission.uasZoneVersion.variantId == ZoneVariantTypes.Customized;
        const isUspace: boolean = submission.uasZoneVersion.typeId == ZoneTypes.Uspace;

        return (!isProhibited || isCustomizedProhibited) && !isUspace;
    }
}
